import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowLeft, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <nav className="glass-header border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COSSY NETWORK
            </span>
          </Link>
          <Link to="/">
            <Button className="btn-primary flex items-center gap-2" data-testid="back-home-btn">
              <Home className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#71717A] hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Privacy Policy
        </h1>

        <div className="prose prose-invert max-w-none">
          <p className="text-[#A1A1AA] mb-6">
            Last Updated: January 2024
          </p>

          <p className="text-[#A1A1AA] mb-8">
            Cossy Network Group LLC ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our trading education platform.
          </p>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              1. Data Collection via Stripe & Forms
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                We collect information when you interact with our platform:
              </p>
              
              <h3 className="text-lg font-bold text-white mt-4">Account Information</h3>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Name and email address when you register</li>
                <li>Encrypted password (we never store plain-text passwords)</li>
                <li>Course enrollment and progress data</li>
              </ul>

              <h3 className="text-lg font-bold text-white mt-4">Payment Information</h3>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Payments are processed securely through <strong className="text-white">Stripe</strong></li>
                <li>We do NOT store your credit card numbers or CVV</li>
                <li>Stripe handles all payment data per PCI-DSS compliance standards</li>
                <li>We only receive confirmation of successful payments and transaction IDs</li>
              </ul>

              <h3 className="text-lg font-bold text-white mt-4">Newsletter & Forms</h3>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Email address when you subscribe to our newsletter</li>
                <li>Contact information provided during consulting bookings</li>
                <li>Any information you voluntarily provide in forms</li>
              </ul>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              2. No Third-Party Sharing
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                <strong className="text-white">We do NOT sell, rent, or share your personal information with third parties for marketing purposes.</strong>
              </p>
              <p>
                Your data is only shared with:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong className="text-white">Stripe</strong> - For payment processing (required to complete purchases)</li>
                <li><strong className="text-white">SendGrid</strong> - For transactional emails (welcome emails, booking confirmations)</li>
                <li><strong className="text-white">Legal authorities</strong> - Only if required by law or valid legal process</li>
              </ul>
              <p>
                We carefully vet all service providers to ensure they maintain appropriate data protection standards.
              </p>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              3. Use of Cookies for Dashboard Sessions
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                We use cookies and similar technologies to provide and improve our services:
              </p>

              <h3 className="text-lg font-bold text-white mt-4">Essential Cookies</h3>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong className="text-white">Authentication cookies</strong> - Keep you logged in to your dashboard</li>
                <li><strong className="text-white">Session cookies</strong> - Maintain your session state while browsing</li>
                <li><strong className="text-white">Security cookies</strong> - Help protect against unauthorized access</li>
              </ul>

              <h3 className="text-lg font-bold text-white mt-4">Preference Cookies</h3>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong className="text-white">Language preference</strong> - Remember your selected language (English, French, or Haitian Creole)</li>
                <li><strong className="text-white">Video progress</strong> - Save your place in course videos</li>
              </ul>

              <p className="mt-4">
                We do NOT use cookies for advertising or tracking across other websites. You can manage cookie preferences in your browser settings, but disabling essential cookies may affect platform functionality.
              </p>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              4. Data Security
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>HTTPS encryption for all data transmission</li>
                <li>Bcrypt password hashing</li>
                <li>Secure HTTP-only cookies for authentication</li>
                <li>Brute force protection on login attempts</li>
                <li>Regular security audits and updates</li>
              </ul>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              5. Your Rights
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Access your personal data</li>
                <li>Update or correct your information</li>
                <li>Request deletion of your account</li>
                <li>Unsubscribe from marketing communications</li>
                <li>Export your course progress data</li>
              </ul>
              <p>
                To exercise these rights, please contact us at support@cossynetworkgroup.com
              </p>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              6. Contact Information
            </h2>
            <div className="text-[#A1A1AA]">
              <p>
                For questions about this Privacy Policy or data practices, contact:
              </p>
              <p className="mt-4">
                <strong className="text-white">Cossy Network Group LLC</strong><br />
                Email: privacy@cossynetworkgroup.com
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Back to Home */}
      <footer className="bg-[#141417] border-t border-[#27272A] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link to="/">
            <Button className="btn-primary flex items-center gap-2 mx-auto" data-testid="footer-back-home">
              <Home className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
          <p className="text-[#71717A] text-sm mt-4">© 2024 Cossy Network Group LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
